import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

type Props = {
  id: string;
  name: string;
  min?: string;
  invalid?: boolean;
  className: string;
  onChange?: (iso: string) => void;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = [['Su', 'Sunday'], ['Mo', 'Monday'], ['Tu', 'Tuesday'], ['We', 'Wednesday'], ['Th', 'Thursday'], ['Fr', 'Friday'], ['Sa', 'Saturday']];

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseIso = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m: m - 1, d };
};
const toDisplay = (iso: string) => {
  const { y, m, d } = parseIso(iso);
  return `${pad(m + 1)}/${pad(d)}/${y}`;
};
const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const shiftIso = (iso: string, days: number) => {
  const { y, m, d } = parseIso(iso);
  const dt = new Date(y, m, d + days);
  return toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
};
const longLabel = (iso: string) => {
  const { y, m, d } = parseIso(iso);
  const wd = WEEKDAYS[new Date(y, m, d).getDay()][1];
  return `${wd}, ${MONTHS[m]} ${d}, ${y}`;
};

export default function DatePicker({ id, name, min, invalid, className, onChange }: Props) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const start = parseIso(value || min || toIso(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()));
  const [view, setView] = useState({ y: start.y, m: start.m });
  const [focusIso, setFocusIso] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open && focusIso) {
      gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${focusIso}"]`)?.focus();
    }
  }, [open, focusIso, view]);

  const openCal = () => {
    const base = value || (min ?? toIso(start.y, start.m, start.d));
    const p = parseIso(base);
    setView({ y: p.y, m: p.m });
    setFocusIso(base);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const pick = (iso: string) => {
    if (min && iso < min) return;
    setValue(iso);
    onChange?.(iso);
    close();
  };

  const moveFocus = (iso: string) => {
    const p = parseIso(iso);
    setView({ y: p.y, m: p.m });
    setFocusIso(iso);
  };

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!focusIso) return;
    const map: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in map) {
      e.preventDefault();
      moveFocus(shiftIso(focusIso, map[e.key]));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(focusIso);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const changeMonth = (delta: number) => {
    const dt = new Date(view.y, view.m + delta, 1);
    setView({ y: dt.getFullYear(), m: dt.getMonth() });
  };

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const total = daysIn(view.y, view.m);
  const cells: (string | null)[] = [...Array(firstDow).fill(null), ...Array.from({ length: total }, (_, i) => toIso(view.y, view.m, i + 1))];
  const minP = min ? parseIso(min) : null;
  const prevDisabled = !!minP && (view.y < minP.y || (view.y === minP.y && view.m <= minP.m));

  return (
    <div ref={wrapRef} className="relative" lang="en-US">
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid}
        aria-label={value ? `Preferred date, ${longLabel(value)}` : 'Preferred date, choose a date'}
        onClick={() => (open ? setOpen(false) : openCal())}
        className={`${className} flex items-center justify-between text-left cursor-pointer`}
      >
        <span className={value ? '' : 'text-foreground-500'}>{value ? toDisplay(value) : 'MM/DD/YYYY'}</span>
        <span className="w-5 h-5 flex items-center justify-center text-foreground-600"><i className="ri-calendar-line" /></span>
      </button>

      {open && (
        <div role="dialog" aria-modal="false" aria-label={`Choose date, ${MONTHS[view.m]} ${view.y}`} className="absolute z-30 left-0 sm:left-auto sm:right-0 mt-2 w-[288px] max-w-[calc(100vw-2rem)] bg-background-100 border border-background-300 p-4">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => changeMonth(-1)} disabled={prevDisabled} aria-label="Previous month" className="w-9 h-9 flex items-center justify-center text-foreground-800 hover:text-primary-400 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"><i className="ri-arrow-left-s-line" /></button>
            <p className="text-sm font-medium text-foreground-950" aria-live="polite">{MONTHS[view.m]} {view.y}</p>
            <button type="button" onClick={() => changeMonth(1)} aria-label="Next month" className="w-9 h-9 flex items-center justify-center text-foreground-800 hover:text-primary-400 cursor-pointer"><i className="ri-arrow-right-s-line" /></button>
          </div>
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map(([s, l]) => <abbr key={s} title={l} className="no-underline text-center text-[11px] uppercase tracking-wider text-foreground-600 py-1">{s}</abbr>)}
          </div>
          <div ref={gridRef} role="grid" className="grid grid-cols-7" onKeyDown={onGridKey}>
            {cells.map((iso, i) => {
              if (!iso) return <span key={`e${i}`} />;
              const disabled = !!min && iso < min;
              const selected = iso === value;
              return (
                <button
                  key={iso}
                  type="button"
                  data-iso={iso}
                  tabIndex={iso === focusIso ? 0 : -1}
                  disabled={disabled}
                  aria-selected={selected}
                  aria-label={longLabel(iso)}
                  onClick={() => pick(iso)}
                  onFocus={() => setFocusIso(iso)}
                  className={`h-9 text-sm transition-colors duration-150 motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-400 ${selected ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'text-foreground-900 hover:bg-background-200'} disabled:opacity-25 disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer`}
                >
                  {parseIso(iso).d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}